import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from "react-dom";
import axios from 'axios';
import DocumentPreview from '../../utils/DocumentPreview';
import GetCookie from "../../utils/GetCookie";
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const DocumentSelector = ({ isOpen, fabricTreeData, selectedDocIds, onDocumentSelect, onClose, sectionName, sectionOrderingId, agentName, agentOrderingId, isEmbedded = false, viewMode = 'collections', onViewModeChange = () => {}, hideViewToggle = false }) => {  const [expandedThreads, setExpandedThreads] = useState({});
  const [localSelectedDocIds, setLocalSelectedDocIds] = useState([]);
  
  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  const [includedDocsMap, setIncludedDocsMap] = useState({}); // Maps threadId to included doc IDs
  const [loadingIncludedDocs, setLoadingIncludedDocs] = useState({}); // Loading state per thread
  const [includedDocsButtonState, setIncludedDocsButtonState] = useState({}); // Track button state per thread
  const [loadingFabricIncludedDocs, setLoadingFabricIncludedDocs] = useState({}); // Loading state per fabric
  const [fabricIncludedDocsLoaded, setFabricIncludedDocsLoaded] = useState({}); // Track which fabrics have loaded included docs
  const [hasLoadedIncludedDocs, setHasLoadedIncludedDocs] = useState(false)

  const loadingInProgressRef = useRef(false);
  const navigate = useNavigate();

  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Initialize local selection state
  useEffect(() => {
    setLocalSelectedDocIds([...selectedDocIds]);
  }, [selectedDocIds]);
  
  // This is the CORRECT hook to load included docs ONCE.
  useEffect(() => {
    if (!hasLoadedIncludedDocs && fabricTreeData && fabricTreeData.length > 0) {
      const fabric = fabricTreeData[0];
      if (fabric && fabric.id && !loadingInProgressRef.current) {
        loadingInProgressRef.current = true;
        loadFabricIncludedDocs(fabric.id).finally(() => {
          loadingInProgressRef.current = false;
          setHasLoadedIncludedDocs(true); // This flag prevents re-running
        });
      }
    }
  }, [fabricTreeData, hasLoadedIncludedDocs]);


  const _normalizeClassification = (cls) => {
    const s = (cls || '').trim();
    if (!s || s.toLowerCase() === 'unclassified') return 'Unclassified';
    return s;
  };

  const getClassificationColorIndex = (name) => {
    if (!name || name === 'Unclassified') return 'unclassified';
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    return (Math.abs(hash) % 7) + 1;
  };

  const displayedThreads = useMemo(() => {
    if (!fabricTreeData || !fabricTreeData.length) return [];
    const fabric = fabricTreeData[0];
    const threads = fabric.threads || [];

    if (viewMode === 'meta_tags') {
      const namedGroups = new Set();
      let hasUnclassifiedDocs = false;

      threads.forEach(thread => {
        (thread.tags || []).forEach(tag => {
          if (typeof tag !== 'string') return;
          const trimmed = tag.trim();
          if (trimmed && trimmed.toLowerCase() !== 'unclassified') {
            namedGroups.add(trimmed);
          }
        });
        (thread.documents || []).forEach(doc => {
          const norm = _normalizeClassification(doc.classification);
          if (norm === 'Unclassified') {
            hasUnclassifiedDocs = true;
          } else {
            namedGroups.add(norm);
          }
        });
      });

      const groups = Array.from(namedGroups)
        .sort((a, b) => a.localeCompare(b))
        .map(name => {
          const docs = [];
          threads.forEach(thread => {
            (thread.documents || []).forEach(doc => {
              if (_normalizeClassification(doc.classification) === name) {
                docs.push(doc);
              }
            });
          });
          return {
            id: `tag-group::${name}`,
            name,
            documents: docs,
            _isTagGroup: true
          };
        });

      if (hasUnclassifiedDocs) {
        const unclassifiedDocs = [];
        threads.forEach(thread => {
          (thread.documents || []).forEach(doc => {
            if (_normalizeClassification(doc.classification) === 'Unclassified') {
              unclassifiedDocs.push(doc);
            }
          });
        });
        groups.push({
          id: 'tag-group::__unclassified__',
          name: 'Unclassified',
          documents: unclassifiedDocs,
          _isTagGroup: true,
          _isUnclassifiedGroup: true
        });
      }

      return groups;
    }
    return threads;
  }, [fabricTreeData, viewMode]);

  const getSubGroupsForThread = (thread) => {
    if (!thread || !thread.documents) return [];

    const namedGroups = new Set();
    let hasUnclassifiedDocs = false;

    (thread.tags || []).forEach(tag => {
      if (typeof tag !== 'string') return;
      const trimmed = tag.trim();
      if (trimmed && trimmed.toLowerCase() !== 'unclassified') {
        namedGroups.add(trimmed);
      }
    });
    thread.documents.forEach(doc => {
      const norm = _normalizeClassification(doc.classification);
      if (norm === 'Unclassified') {
        hasUnclassifiedDocs = true;
      } else {
        namedGroups.add(norm);
      }
    });

    const subGroups = Array.from(namedGroups)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({
        id: `${thread.id}::${name}`,
        name,
        documents: thread.documents.filter(
          doc => _normalizeClassification(doc.classification) === name
        )
      }));

    if (hasUnclassifiedDocs) {
      subGroups.push({
        id: `${thread.id}::__unclassified__`,
        name: 'Unclassified',
        documents: thread.documents.filter(
          doc => _normalizeClassification(doc.classification) === 'Unclassified'
        ),
        _isUnclassifiedGroup: true
      });
    }

    return subGroups;
  };

  useEffect(() => {
    if ((isOpen || isEmbedded) && displayedThreads.length > 0) {
      const expansions = {};
      displayedThreads.forEach(thread => {
        expansions[thread.id] = true;
      });
      setExpandedThreads(expansions);
    }
  }, [isOpen, isEmbedded, displayedThreads]);

  // Memoize per-group selection states using whichever grouping is currently displayed
  const threadStates = useMemo(() => {
    const states = {};
    
    displayedThreads.forEach(thread => {
      const threadDocIds = thread.documents?.map(doc => doc.id) || [];
      const selectedCount = threadDocIds.filter(docId => localSelectedDocIds.includes(docId)).length;
      
      states[thread.id] = {
        isSelected: threadDocIds.length > 0 && selectedCount === threadDocIds.length,
        isPartial: selectedCount > 0 && selectedCount < threadDocIds.length,
        docIds: threadDocIds
      };
    });
    
    return states;
  }, [displayedThreads, localSelectedDocIds]);

  // Calculate total document count
  const totalDocumentCount = useMemo(() => {
    if (!fabricTreeData || !fabricTreeData.length) return 0;
    const fabric = fabricTreeData[0];
    return fabric.threads?.reduce((count, thread) => {
      return count + (thread.documents?.length || 0);
    }, 0) || 0;
  }, [fabricTreeData]);

  // Toggle thread expansion
  const toggleThreadExpansion = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };

  // Handle thread selection (select/deselect all documents in thread)
  const handleThreadSelection = (threadId, checked) => {
    const threadState = threadStates[threadId];
    if (!threadState) return;

    let newSelection = [...localSelectedDocIds];

    if (checked) {
      // Add all documents from this thread
      threadState.docIds.forEach(docId => {
        if (!newSelection.includes(docId)) {
          newSelection.push(docId);
        }
      });
    } else {
      // Remove all documents from this thread
      newSelection = newSelection.filter(id => !threadState.docIds.includes(id));
    }

    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }
    // Reset included docs button state for this thread
    setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'normal' }));
  };

  // Handle individual document selection
  const handleDocumentSelection = (docId, checked) => {
    let newSelection = [...localSelectedDocIds];

    if (checked) {
      if (!newSelection.includes(docId)) {
        newSelection.push(docId);
      }
    } else {
      newSelection = newSelection.filter(id => id !== docId);
    }

    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }
    
    // Reset included docs button state for the thread that contains this document
    const fabric = fabricTreeData[0];
    fabric.threads?.forEach(thread => {
      const docExists = thread.documents?.some(doc => doc.id === docId);
      if (docExists) {
        setIncludedDocsButtonState(prev => ({ ...prev, [thread.id]: 'normal' }));
      }
    });
  };

  // Sub-group (View 3 classification group inside a thread) selection helpers
  const getSubGroupSelectionState = (subGroup) => {
    const docIds = (subGroup.documents || []).map(d => d.id);
    if (docIds.length === 0) {
      return { isSelected: false, isPartial: false, docIds: [] };
    }
    const selectedCount = docIds.filter(id => localSelectedDocIds.includes(id)).length;
    return {
      isSelected: selectedCount === docIds.length,
      isPartial: selectedCount > 0 && selectedCount < docIds.length,
      docIds
    };
  };

  const handleSubGroupSelection = (subGroup, checked) => {
    const docIds = (subGroup.documents || []).map(d => d.id);
    if (docIds.length === 0) return;
    let newSelection;
    if (checked) {
      newSelection = Array.from(new Set([...localSelectedDocIds, ...docIds]));
    } else {
      newSelection = localSelectedDocIds.filter(id => !docIds.includes(id));
    }
    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }
  };

  // Handle Select Included Docs functionality
  const handleSelectIncludedDocs = async (fabricId, threadId) => {
    const fabric = fabricTreeData[0];
    const thread = fabric.threads?.find(t => t.id === threadId);
    if (!thread) return;

    const threadDocs = thread.documents?.map(doc => doc.id) || [];
    const currentIncludedDocs = includedDocsMap[threadId] || [];
    const currentButtonState = includedDocsButtonState[threadId] || 'normal';
    
    // If button is in "included_selected" state, clicking again selects all docs and resets
    if (currentButtonState === 'included_selected') {
      // Select all docs in the thread
      let newSelection = [...localSelectedDocIds];
      
      // Remove all documents from this thread first
      newSelection = newSelection.filter(id => !threadDocs.includes(id));
      
      // Add all documents from this thread
      threadDocs.forEach(docId => {
        if (!newSelection.includes(docId)) {
          newSelection.push(docId);
        }
      });
      
      setLocalSelectedDocIds(newSelection);
      if (isEmbedded) {
        onDocumentSelect(newSelection);
      }
      setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'normal' }));
      return;
    }
    
    // Check if we have included docs for this thread (should always be true due to shouldShowIncludedDocsButton)
    if (currentIncludedDocs.length === 0) {
      alert("No included documents found for this thread");
      return;
    }

    // Select only the included documents, unselect others from this thread
    let newSelection = [...localSelectedDocIds];
    
    // Remove all documents from this thread first
    newSelection = newSelection.filter(id => !threadDocs.includes(id));
    
    // Add only the included documents
    currentIncludedDocs.forEach(docId => {
      if (!newSelection.includes(docId)) {
        newSelection.push(docId);
      }
    });
    
    setLocalSelectedDocIds(newSelection);
    if (isEmbedded) {
      onDocumentSelect(newSelection);
    }
    setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'included_selected' }));
  };

  // Batch load included documents for all threads in a fabric (called only once)
  const loadFabricIncludedDocs = async (fabricId) => {
    const fabric = fabricTreeData[0];
    if (!fabric || fabric.id !== fabricId || fabricIncludedDocsLoaded[fabricId] || loadingFabricIncludedDocs[fabricId]) {
      loadingInProgressRef.current = false;
      return;
    }

    if (!fabric.threads || fabric.threads.length === 0) {
      loadingInProgressRef.current = false;
      return;
    }

    setLoadingFabricIncludedDocs(prev => ({ ...prev, [fabricId]: true }));

    try {
      // Create batch requests for all threads in this fabric
      const threadRequests = fabric.threads.map(thread => 
        axios.post(
          `pceapi/fabrics/slr_included_documents`,
          { 
            thread_id: thread.id,
            fabric_id: fabricId
          },
          { headers }
        ).catch(error => ({ 
          threadId: thread.id, 
          error: error.message 
        }))
      );

      // Execute all requests in parallel
      const responses = await Promise.all(threadRequests);
      
      // Process responses and update included docs map
      const newIncludedDocs = { ...includedDocsMap };
      
      responses.forEach((response, index) => {
        const thread = fabric.threads[index];
        
        if (response.error) {
          console.warn(`Failed to load included docs for Data Collection ${thread.id}:`, response.error);
          return;
        }

        const data = response.data;
        
        if (data.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }
        
        if (data.status === 'success' && data.included_doc_ids) {
          const includedDocIds = data.included_doc_ids || [];
          const threadDocs = thread.documents?.map(doc => doc.id) || [];
          
          // Filter to only include docs that exist in this thread
          const validIncludedDocs = includedDocIds.filter(docId => threadDocs.includes(docId));
          
          if (validIncludedDocs.length > 0) {
            newIncludedDocs[thread.id] = validIncludedDocs;
          }
        }
      });

      // Update state with all new included docs
      setIncludedDocsMap(newIncludedDocs);
      setFabricIncludedDocsLoaded(prev => ({ ...prev, [fabricId]: true }));

    } catch (error) {
      console.error("Error batch loading included documents:", error);
    } finally {
      setLoadingFabricIncludedDocs(prev => ({ ...prev, [fabricId]: false }));
      loadingInProgressRef.current = false;
    }
  };

  const shouldShowIncludedDocsButton = (fabricId, threadId) => {
    if (loadingFabricIncludedDocs[fabricId]) return false;

    const displayed = displayedThreads.find(t => t.id === threadId);
    if (displayed && displayed._isTagGroup) return false;
    const includedDocs = includedDocsMap[threadId] || [];
    return includedDocs.length > 0;
  };

  // Get the button text and icon for included docs
  const getIncludedDocsButtonContent = (threadId) => {
    const buttonState = includedDocsButtonState[threadId] || 'normal';
    const isLoading = loadingIncludedDocs[threadId] || false;

    if (isLoading) {
      return {
        icon: "bi-hourglass-split",
        text: "Loading...",
        disabled: true
      };
    }

    if (buttonState === 'included_selected') {
      return {
        icon: "bi-check-circle",
        text: "Included Selected",
        disabled: false
      };
    }

    return {
      icon: "bi-filter",
      text: "Select SLR included Docs",
      disabled: false
    };
  };

  // Select all documents
  const selectAllDocuments = () => {
    const allDocIds = [];
    fabricTreeData.forEach(fabric => {
      fabric.threads?.forEach(thread => {
        thread.documents?.forEach(doc => {
          allDocIds.push(doc.id);
        });
      });
    });
    setLocalSelectedDocIds(allDocIds);
    if (isEmbedded) {
      onDocumentSelect(allDocIds);
    }
    // Reset all included docs button states
    setIncludedDocsButtonState({});
  };

  // Deselect all documents
  const deselectAllDocuments = () => {
    setLocalSelectedDocIds([]);
    if (isEmbedded) {
      onDocumentSelect([]);
    }
    // Reset all included docs button states
    setIncludedDocsButtonState({});
  };

  // Enhanced close handler - automatically saves selection
  const handleClose = () => {
    // Auto-save the current selection
    onDocumentSelect(localSelectedDocIds);
    onClose();
  };

  // Handle document preview
  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  // Close document preview
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };

const renderViewToggle = () => (
  <div className="d-flex align-items-center ms-2">
    <div className="ev-view-toggle-shell">
      <button
        type="button"
        className={`ev-view-toggle-btn ${viewMode === 'collections' ? 'active' : ''}`}
        onClick={() => onViewModeChange('collections')}
      >
        Collections
      </button>

      <button
        type="button"
        className={`ev-view-toggle-btn ${viewMode === 'meta_tags' ? 'active' : ''}`}
        onClick={() => onViewModeChange('meta_tags')}
      >
        Meta Tags
      </button>

      <button
        type="button"
        className={`ev-view-toggle-btn ${viewMode === 'both' ? 'active' : ''}`}
        onClick={() => onViewModeChange('both')}
      >
        Both
      </button>
    </div>
  </div>
);

  const renderClassificationBadge = (name) => {
    const idx = getClassificationColorIndex(name);
    const variantClass = idx === 'unclassified'
      ? 'ev-tag-badge-unclassified'
      : `ev-tag-badge-${idx}`;
    return (
      <span className={`ev-tag-badge ${variantClass}`}>
        {name}
      </span>
    );
  };

  if (!isOpen && !isEmbedded) return null;

  const fabric = fabricTreeData[0];
  if (!fabric) {
    return (
      <div className="p-3">
        <div className="ev-alert-card-warning">
          <p className="mb-0">
            <i className="bi bi-info-circle me-1"></i>
            No fabric selected. Please select a fabric to view documents.
          </p>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!isEmbedded && createPortal(
        <div 
          className="modal fade modal-high-priority show d-block bg-dark bg-opacity-50"
          style={{ zIndex: "var(--num-1000000000)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content ev-message-card2 d-block ev-card-2">
              <div className="row mb-0 modal-header d-flex align-items-center border-0">
                  <div className="col-11">
                    <span className="ev-reg-p-dark mb-2">
                      <i className="bi bi-boxes"></i> {fabric.name} 
                      <span className="badge ev-message-card2 ms-2 ev-fs-11">
                        Section {sectionOrderingId}. Agent {agentOrderingId}.
                      </span>
                    </span>
                      <button 
                          type="button"
                          className="ev-btn-blue-outline-round ev-bold-reg  ev-btn-small ms-2"
                          onClick={selectAllDocuments}
                        >
                          Select All
                      </button>
                      <button 
                          type="button"
                          className="ev-btn-blue-outline-round ev-bold-reg  ev-btn-small ms-2"
                          onClick={deselectAllDocuments}
                        >
                          Deselect All
                      </button>
                      {/* Document count display instead of manual save button */}
                      <span className="badge bg-primary ms-2 ev-btn-reg">
                        {localSelectedDocIds.length} of {totalDocumentCount} selected
                      </span>
                  </div>
                  <div className="col-1 text-end" >
                    <button 
                      type="button" 
                      className="ev-btn-text-only-fill ev-fs-20"  
                      onClick={handleClose}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
              </div>
              <div className="scrollable-element vh-60 ev-padding-small">
                {/* Threads and Documents */}
                <div>
                  {fabric.threads?.map(thread => {
                    const threadState = threadStates[thread.id] || { isSelected: false, isPartial: false };
                    const buttonContent = getIncludedDocsButtonContent(thread.id);
                    
                    return (
                      <div key={thread.id} className="ev-card-3 mb-2">
                        <div className="d-flex align-items-center">
                          <button 
                            className="btn btn-sm p-0 me-2 w-3" 
                            onClick={() => toggleThreadExpansion(thread.id)}
                          >
                            <i className={`ev-reg-p-dark bi ${expandedThreads[thread.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                          </button>
                          
                          <div className="form-check d-flex align-items-center">
                            <input
                              className="form-check-input me-2 position-relative"
                              type="checkbox"
                              checked={threadState.isSelected}
                              onChange={(e) => handleThreadSelection(thread.id, e.target.checked)}
                              id={`thread-${thread.id}`}
                              ref={input => {
                                if (input) {
                                  input.indeterminate = threadState.isPartial;
                                }
                              }}
                            />
                            <label 
                              className="form-check-label ev-small-p-blue avatar-tooltip ev-bold-reg" 
                              data-tooltip={thread.name}
                              htmlFor={`thread-${thread.id}`}
                            >
                              {thread.name.length > 30
                                ? thread.name.substring(0, 30) + '...' 
                              : thread.name}
                            </label>
                            <span className="badge bg-warning ms-2 ev-btn-small">
                              {thread.documents?.length || 0} docs
                            </span> &nbsp;&nbsp;
                            {shouldShowIncludedDocsButton(fabric.id, thread.id) && (
                              <button 
                                className={`ev-btn-blue-fill-round ev-btn-small  ev-bold-reg text-nowrap pt-1 pb-1 ${includedDocsButtonState[thread.id] === 'included_selected' ? 'active' : ''}`}
                                onClick={() => handleSelectIncludedDocs(fabric.id, thread.id)}
                                disabled={buttonContent.disabled}
                              >
                                <i className={`bi ${buttonContent.icon} me-1`}></i>
                                {buttonContent.text}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Select Included Docs button */}
                        <div className="d-flex justify-content-start align-items-center mt-1 mb-1">
                          
                        </div>
                        
                        {expandedThreads[thread.id] && (
                          <div className="mt-2 ms-5">
                            {thread.documents?.map(doc => (
                              <div key={doc.id}>
                              <span className="ev-message-card1 d-inline-flex pe-1 mb-1" >
                                <div className="d-flex align-items-center">
                                  <input
                                    className="form-check-input me-2 mt-0"
                                    type="checkbox"
                                    checked={localSelectedDocIds.includes(doc.id)}
                                    onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                    id={`doc-${doc.id}`}
                                  />
                                  <label 
                                    className="form-check-label ev-xsmall-p-blue avatar-tooltip" 
                                    htmlFor={`doc-${doc.id}`}
                                    data-tooltip={doc.document_name}
                                  >
                                    {doc.document_name.length > 70 
                                      ? doc.document_name.substring(0, 70) + '...' 
                                      : doc.document_name}
                                  </label>
                                </div>
                                
                                <button 
                                  type="button"
                                  onClick={() => handlePreviewDocument(doc.id, doc.document_name)} 
                                  className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0"
                                >
                                  View <i className="bi bi-eye"></i> 
                                </button> 
                              </span>
                              </div>
                            ))}
                            
                            {(!thread.documents || thread.documents.length === 0) && (
                              <p className="ev-small-p-blue text-center mt-2 mb-2">No documents in this Data Collection</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {(!fabric.threads || fabric.threads.length === 0) && (
                    <div className="text-center p-3">
                      <p className="ev-small-p-blue">No Data Collections found in this fabric</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {isEmbedded && (
        <div className="scrollable-element vh-50 ev-padding-small" >
          {/* Threads and Documents - same as above */}
          <div className="d-flex flex-column mb-2">
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center overflow-visible">
                <span
                  className="avatar-tooltip d-inline-block"
                  data-tooltip={fabric.name}
                >
                  <span
                    className="ev-reg-p-dark d-inline-block text-truncate"
                    style={{ maxWidth: "var(--px-300)" }}
                  >
                  <i className="bi bi-boxes"></i> {fabric.name}

                  {/* Section + Agent Ordering */}
                  {sectionOrderingId && agentOrderingId && (
                    <span className="badge ev-message-card2 ms-2 ev-fs-11">
                      Section {sectionOrderingId}. Agent {agentOrderingId}.
                    </span>
                  )}

                  {/* Section Name */}
                  {sectionName && (
                    <span className="badge bg-info ms-2 ev-fs-11">
                      {sectionName}
                    </span>
                  )}

                  {/* Agent Name */}
                  {agentName && (
                    <span className="badge bg-secondary ms-2 ev-fs-11">
                      {agentName}
                    </span>
                  )}
                </span>
                </span>
              </div>

              {hideViewToggle !== true && (
              <div className="flex-shrink-0 mx-2">
                {renderViewToggle()}
              </div>
            )}
                
              {/* Action Buttons */}
              <div className="text-end flex-shrink-0 ms-auto">
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
                  onClick={selectAllDocuments}
                >
                  Select All
                </button>
                
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small ms-2"
                  onClick={deselectAllDocuments}
                >
                  Deselect All
                </button>
                
                <span className="badge bg-primary ms-2 ev-btn-reg">
                  {localSelectedDocIds.length} of {totalDocumentCount} selected
                </span>
              </div>
            </div>
          </div>
          <div>
            {displayedThreads.map(thread => {
              const threadState = threadStates[thread.id] || { isSelected: false, isPartial: false };
              const buttonContent = getIncludedDocsButtonContent(thread.id);
              const isTagGroup = !!thread._isTagGroup;
              const subGroups = (viewMode === 'both' && !isTagGroup && expandedThreads[thread.id])
                ? getSubGroupsForThread(thread)
                : null;

              return (
                <div key={thread.id} className="mb-2 ev-card-3">
                  <div className="d-flex align-items-center">
                    <button 
                      className="btn btn-sm p-0 me-2 w-3" 
                      onClick={() => toggleThreadExpansion(thread.id)}
                    >
                      <i className={`ev-reg-p-dark bi ${expandedThreads[thread.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                    </button>
                    
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input me-2 position-relative mt-0"
                        type="checkbox"
                        checked={threadState.isSelected}
                        onChange={(e) => handleThreadSelection(thread.id, e.target.checked)}
                        id={`thread-${thread.id}`}
                        ref={input => {
                          if (input) {
                            input.indeterminate = threadState.isPartial;
                          }
                        }}
                      />
                      {isTagGroup ? (
                        <label
                          className="form-check-label ms-1"
                          htmlFor={`thread-${thread.id}`}
                        >
                          {renderClassificationBadge(thread.name)}
                        </label>
                      ) : (
                        <label 
                          className="form-check-label ev-small-p-blue avatar-tooltip ev-bold-reg"
                          data-tooltip={thread.name}
                          htmlFor={`thread-${thread.id}`}
                        >
                          {thread.name.length > 30
                            ? thread.name.substring(0, 30) + '...' 
                            : thread.name}
                        </label>
                      )}
                      <span className="badge bg-warning ms-2 ev-btn-small">
                        {thread.documents?.length || 0} docs
                      </span> &nbsp;&nbsp;
                      {shouldShowIncludedDocsButton(fabric.id, thread.id) && (
                        <button 
                          className={`ev-btn-blue-fill-round ev-btn-small pt-1 pb-1  ev-bold-reg text-nowrap ${includedDocsButtonState[thread.id] === 'included_selected' ? 'active' : ''}`}
                          onClick={() => handleSelectIncludedDocs(fabric.id, thread.id)}
                          disabled={buttonContent.disabled}
                        >
                          <i className={`bi ${buttonContent.icon} me-1`}></i>
                          {buttonContent.text}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Spacer row */}
                  <div className="d-flex justify-content-start align-items-center mt-1 mb-1">
                    
                  </div>

                  {/* ───── View 3 ('both'): sub-groups by classification ───── */}
                  {subGroups && (
                    <div className="ms-5 mt-1">
                      {subGroups.map(subGroup => {
                        const subState = getSubGroupSelectionState(subGroup);
                        const subDocCount = subGroup.documents.length;
                        return (
                          <div key={subGroup.id} className="mb-2">
                            {/* Sub-group header row */}
                            <div className="form-check d-flex align-items-center">
                              <input
                                className="form-check-input me-2 mt-0"
                                type="checkbox"
                                checked={subState.isSelected}
                                onChange={(e) => handleSubGroupSelection(subGroup, e.target.checked)}
                                id={`subgroup-${subGroup.id}`}
                                // disabled={subDocCount === 0}
                                ref={input => {
                                  if (input) {
                                    input.indeterminate = subState.isPartial;
                                  }
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`subgroup-${subGroup.id}`}
                              >
                                {renderClassificationBadge(subGroup.name)}
                              </label>
                              <span className="badge bg-warning ms-2 ev-btn-small">
                                {subDocCount} docs
                              </span>
                            </div>

                            {/* Documents inside this sub-group (further indented) */}
                            {subDocCount > 0 ? (
                              <div className="ms-4 mt-1">
                                {subGroup.documents.map(doc => (
                                  <div key={doc.id}>
                                    <span className="ev-message-card1 d-inline-flex pe-1 mb-1">
                                      <div className="d-flex align-items-center">
                                        <input
                                          className="form-check-input me-2 mt-0"
                                          type="checkbox"
                                          checked={localSelectedDocIds.includes(doc.id)}
                                          onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                          id={`doc-${doc.id}`}
                                        />
                                        <label
                                          className="form-check-label ev-xsmall-p-blue avatar-tooltip"
                                          htmlFor={`doc-${doc.id}`}
                                          data-tooltip={doc.document_name}
                                        >
                                          {doc.document_name.length > 50
                                            ? doc.document_name.substring(0, 50) + '...'
                                            : doc.document_name}
                                        </label>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handlePreviewDocument(doc.id, doc.document_name)}
                                        className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0"
                                      >
                                        View <i className="bi bi-eye"></i>
                                      </button>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="ev-small-p-blue ms-4 mt-2 mb-2">
                                No documents found
                              </p>
                            )}
                          </div>
                        );
                      })}

                      {subGroups.length === 0 && (
                        <p className="ev-small-p-blue text-center mt-2 mb-2">No documents in this Data Collection</p>
                      )}
                    </div>
                  )}

                  {/* ───── View 1 / View 2: flat doc list (unchanged) ───── */}
                  {!subGroups && expandedThreads[thread.id] && (
                    <div className="mt-2 ms-5">
                      {thread.documents?.map(doc => (
                        <div key={doc.id}>
                        <span className="ev-message-card1 d-inline-flex pe-1 mb-1">
                          <div className="d-flex align-items-center">
                            <input
                              className="form-check-input me-2 mt-0"
                              type="checkbox"
                              checked={localSelectedDocIds.includes(doc.id)}
                              onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                              id={`doc-${doc.id}`}
                            />
                            <label 
                              className="form-check-label ev-xsmall-p-blue avatar-tooltip" 
                              htmlFor={`doc-${doc.id}`}
                              data-tooltip={doc.document_name}
                            >
                              {doc.document_name.length > 50 
                                ? doc.document_name.substring(0, 50) + '...' 
                                : doc.document_name}
                            </label>
                          </div>
                          
                          <button 
                            type="button"
                            onClick={() => handlePreviewDocument(doc.id, doc.document_name)} 
                            className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-2 me-0"
                          >
                            View <i className="bi bi-eye"></i> 
                          </button> 
                        </span>
                        </div>
                      ))}
                      
                      {(!thread.documents || thread.documents.length === 0) && (
                        <p className="ev-small-p-blue text-center mt-2 mb-2">No documents in this Data Collection</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {displayedThreads.length === 0 && (
              <div className="text-center p-3">
                <p className="ev-small-p-blue">No Data Collections found in this fabric</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Preview Modal - shared for both modes */}
      <DocumentPreview 
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />
    </>
  );

  if (!isEmbedded) {
    return content;
  }

  return content;
};

export default DocumentSelector;